import { BaseViewModel } from '../../../../../models/base/base-view-model';
import { BehaviorSubject, combineLatest, iif, Observable, of } from 'rxjs';
import { AlertTypeEnum } from '../../../../../models/alerts/enum/alert-type.enum';
import { filter, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { Alert } from '../../../../../models/alerts/dto/alert';
import { AlertsDomainModel } from '../../../../../domainModels/alerts-domain-model';
import { inject } from '@angular/core';
import { RadioButton } from '../../../../../models/shared/radio-button';
import { AlertBackgroundColorEnum } from '../../../../../models/alerts/enum/alert-background-color.enum';
import { CreateAlertRequest } from '../../../../../models/alerts/requests/create-alert-request';
import { ToastService } from '../../../../../services/toast-service';
import { Router } from '@angular/router';
import { DateUtils } from '../../../../../utils/date-utils';
import { LoadingOptions } from '../../../../../models/shared/loading-options';
import { PermissionService } from 'src/app/services/permission-service';
import { AlertStatusStringEnum } from '../../../../../models/alerts/enum/alert-status.enum';
import { StatusPill } from '../../../../../models/shared/status-pill';

export class AlertDetailsViewModel extends BaseViewModel {
  constructor() {
    super();
  }

  private alertDomainModel = inject(AlertsDomainModel);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private permissionService = inject(PermissionService);

  public _loadingOpts = new BehaviorSubject<LoadingOptions>(LoadingOptions.default(false, true));
  public loadingOpts$ = this._loadingOpts as Observable<LoadingOptions>;

  public readonly canEditAlertDetails$ = this.permissionService.permissionGranted$([1029]);

  private _alertFormDirty = new BehaviorSubject<boolean>(false);
  public alertFormDirty$ = this._alertFormDirty.asObservable();
  public setAlertFormDirty = (value: boolean) => this._alertFormDirty.next(value);

  private _messageStyleChanged = new BehaviorSubject<boolean>(false);
  public messageStyleChanged$ = this._messageStyleChanged.asObservable();
  public setMessageStyleChanged = (value: boolean) => this._messageStyleChanged.next(value);

  private _previewLoading = new BehaviorSubject<boolean>(false);
  public previewLoading$ = this._previewLoading.asObservable();
  public setPreviewLoading = (value: boolean) => this._previewLoading.next(value);

  private _includeButton = new BehaviorSubject<boolean>(true);
  public includeButton$ = this._includeButton.asObservable();
  public setIncludeButton = (value: boolean) => this._includeButton.next(value);

  public cardTypes$ = this.alertDomainModel.alertTypes$;

  public cardAlertColors$ = this.cardTypes$.pipe(
    map(cts => {
      return cts.find(ct => ct.id === AlertTypeEnum.Card)?.colours ?? [];
    })
  );

  public bannerAlertColors$ = this.cardTypes$.pipe(
    map(cts => {
      return cts.find(ct => ct.id === AlertTypeEnum.Banner)?.colours ?? [];
    })
  );

  public cardRadioButtons$ = this.cardAlertColors$.pipe(
    map(colors => {
      return colors.map(c => {
        return new RadioButton(c.name, c.id);
      });
    })
  );

  public bannerRadioButtons$ = this.bannerAlertColors$.pipe(
    map(colors => {
      return colors.map(c => {
        return new RadioButton(c.name, c.id);
      });
    })
  );

  private _selectedAlertType = new BehaviorSubject<AlertTypeEnum>(AlertTypeEnum.Card);
  public selectedAlertType$ = this._selectedAlertType.asObservable();

  private _alert = new BehaviorSubject<Alert>(new Alert());
  public alert$ = this._alert.asObservable();

  private _alertPreviewPristine = new BehaviorSubject<boolean>(true);
  public alertPreviewPristine$ = this._alertPreviewPristine.asObservable();
  public setAlertPreviewPristine = (value: boolean) => this._alertPreviewPristine.next(value);

  private _alertPreview = new BehaviorSubject<Alert>(new Alert());
  public alertPreview$ = combineLatest([this._alertPreview, this.alertPreviewPristine$]).pipe(
    map(([alert, pristine]) => {
      if (pristine) {
        alert.title = $localize`Title`;
        alert.description = $localize`Description`;
        alert.buttonText = $localize`Button Text`;
      }
      return alert;
    })
  );

  private _showAlertPreview = new BehaviorSubject<boolean>(true);
  public showAlertPreview$ = this._showAlertPreview.asObservable();
  public setShowAlertPreview = (value: boolean) => this._showAlertPreview.next(value);

  public isCardTypeAlert$ = this.selectedAlertType$.pipe(map(alertType => alertType === AlertTypeEnum.Card));

  private _alertId = new BehaviorSubject<string | null>(null);
  public readonly alertId$ = this._alertId.asObservable();

  private loadingMessage = $localize`Loading Alert`;

  public statusPill$ = this.alert$.pipe(
    map(a => {
      if (a?.status) {
        switch (a.status) {
          case AlertStatusStringEnum.Active:
            return new StatusPill(1, $localize`Active`, '#78AB00', '#E8F7D9', '#395200');
          case AlertStatusStringEnum.Past:
            return new StatusPill(2, $localize`Past`, '#B9B9B9', '#FAFAFA', '#3B3B3C');
          case AlertStatusStringEnum.Upcoming:
            return new StatusPill(3, $localize`Scheduled`, '#68C3EB', '#DCEBFA', '#104255');
        }
      }
    })
  );

  public alertStartDate$ = this.alert$.pipe(map(a => DateUtils.formatDateStringToCSTDate(a?.startDate ?? '')));

  public alertEndDate$ = this.alert$.pipe(map(a => DateUtils.formatDateStringToCSTDate(a?.endDate ?? '')));

  public alertLastUpdatedDate$ = this.alert$.pipe(
    map(a => DateUtils.formatDateStringToCSTDate(a?.lastUpdatedDate ?? ''))
  );

  private _isEditingAlert = new BehaviorSubject<boolean>(false);
  public isEditingAlert$ = this._isEditingAlert.asObservable();

  protected listenToAlertId = this.alertId$
    .pipe(
      filter(alertId => !!alertId),
      switchMap(alertUserId => {
        if (alertUserId !== null) {
          this._loadingOpts.addRequest(this.loadingMessage);
          return this.alertDomainModel.getAlertById(alertUserId);
        } else {
          this.toastService.publishErrorMessage($localize`Error!`, $localize`Invalid Alert ID!`);
          this.router.navigate(['/settings/alerts']).then();
          return of(null);
        }
      }),
      takeUntil(this.onDestroy),
      finalize(() => {
        this._loadingOpts.removeRequest(this.loadingMessage);
      })
    )
    .subscribe({
      next: alert => {
        this._loadingOpts.removeRequest(this.loadingMessage);
        if (!!alert) {
          const buttonExists = !!alert?.buttonText || !!alert?.buttonLink;
          this.setIncludeButton(buttonExists);
          alert.includeButton = buttonExists;
          this._alert.next(alert);
          this.setAlertType(alert.typeId);
        } else {
          this.toastService.publishErrorMessage($localize`Error!`, $localize`Invalid Alert ID!`);
          this.router.navigate(['/settings/alerts']).then();
        }
      },
      error: err => {
        if (err.code === 404) {
          this.toastService.publishErrorMessage($localize`Error!`, $localize`Invalid Alert ID!`);
        } else {
          this.toastService.publishError(err);
        }
        this._loadingOpts.removeRequest(this.loadingMessage);
        this.router.navigate(['/settings/alerts']).then();
      }
    });

  public setAlertType(alertType: AlertTypeEnum): void {
    this._selectedAlertType.next(alertType);
    this.setPreviewLoading(true);
    setTimeout(() => this.setPreviewLoading(false), 1000);
  }

  public setAlertId(alertId: string): void {
    this._alertId.next(alertId);
  }

  public updatePreview(alert: Alert): void {
    this.checkForColorAndUpdatePreview(alert);
  }

  public createAlert(alert: Alert): void {
    alert.clearInvalidProperties();
    const req = CreateAlertRequest.hydrateFromAlert(alert);
    const lm = $localize`Creating Alert`;
    this._loadingOpts.addRequest(lm);
    this.alertDomainModel.createAlert(req).subscribe({
      next: a => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishSuccessMessage($localize`Success!`, $localize`Alert Created`);
        this.setAlertFormDirty(false);
        this.setMessageStyleChanged(false);
        this.router.navigate(['/settings/alerts']).then();
      },
      error: err => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishError(err);
      }
    });
  }

  public updateAlert(alert: Alert): void {
    const lm = $localize`Updating Alert`;
    alert.clearInvalidProperties();
    alert.setDates();
    this._loadingOpts.addRequest(lm);
    this.alertDomainModel.updateAlert(alert).subscribe({
      next: a => {
        a.includeButton = !!a.buttonText || !!a.buttonLink;
        this._alert.next(a);
        this.toastService.publishSuccessMessage($localize`Success!`, $localize`Alert Updated`);
        this._loadingOpts.removeRequest(lm);
        this._isEditingAlert.next(false);
        this.setAlertFormDirty(false);
      },
      error: err => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishError(err);
      }
    });
  }
  public deleteAlert(id: string): void {
    const lm = $localize`Deleting Alert`;
    this._loadingOpts.addRequest(lm);
    this.alertDomainModel.deleteAlert(id).subscribe({
      next: () => {
        this.toastService.publishSuccessMessage($localize`The alert has been deleted`);
        this.router.navigate(['/settings/alerts']).then();
        this._loadingOpts.removeRequest(lm);
      },
      error: err => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishError(err);
      }
    });
  }

  private checkForColorAndUpdatePreview(alert: Alert): void {
    // Ensuring a color is set on the alert preview, if not set default of green
    if (!alert.colourId) {
      alert.colourId =
        alert.typeId === AlertTypeEnum.Card ? AlertBackgroundColorEnum.CardPurple : AlertBackgroundColorEnum.BannerBlue;
    }
    this.isCardTypeAlert$
      .pipe(switchMap(isCardTypeAlert => iif(() => isCardTypeAlert, this.cardAlertColors$, this.bannerAlertColors$)))
      .once(colors => {
        const color = colors?.find(c => c?.id === alert?.colourId);
        if (color) {
          alert.colour.backgroundColour = color?.backgroundColour;
          alert.colour.accentColour = color?.accentColour;
        }
        this._alertPreview.next(alert);
      });
  }

  public toggleIsEditingAlert(): void {
    this.isEditingAlert$.once(isEditing => this._isEditingAlert.next(!isEditing));
  }
}
